@if (addPosition() === "top" && addable && newValue()) {
  <div class="table-header-actions">
    <button
      pButton
      data-cy="add"
      size="small"
      severity="success"
      (click)="onAddRow()"
    >
      <i class="pi pi-plus"></i> {{ addLabel() }}
    </button>
    <ng-content select="[tableHeaderActions]" />
  </div>
}

<p-table
  [columns]="visibleColumns()"
  [value]="skeletonRows()"
  [paginator]="paginator() || showRecordCounter()"
  [showCurrentPageReport]="paginator() && !showRecordCounter()"
  currentPageReportTemplate="{first} to {last} of {totalRecords} entries"
  paginatorDropdownAppendTo="body"
  [rows]="rows"
  [totalRecords]="totalRecords"
  [rowsPerPageOptions]="rowsPerPageOptions()"
  [selection]="selection"
  (selectionChange)="onSelectionChange($event)"
  [selectionMode]="selectable() ? 'multiple' : null"
  (onPage)="onPageChange($event)"
  (onSort)="sortRows.emit($event)"
  (onRowSelect)="onSelectRow($event)"
  (onRowUnselect)="onUnselectRow($event)"
  (onRowReorder)="onReorder($event)"
  [first]="first"
  [contextMenuSelection]="contextMenuSelection()"
  (contextMenuSelectionChange)="contextMenuSelectionChange.emit($event)"
  [contextMenu]="contextMenuItems() ? cm : null"
  responsiveLayout="scroll"
  [dataKey]="dataKey()"
  lazy="true"
>
  <ng-template #header>
    <tr>
      @if (selectable()) {
        <th class="column column--selection">
          @if (headerSelectable()) {
            <p-tableheadercheckbox> </p-tableheadercheckbox>
          }
        </th>
      }
      @for (column of visibleColumns(); track column) {
        <th
          class="column--{{ column.type }} column--{{ column.field }}"
          [pSortableColumn]="column.containerName || column.field"
          [pSortableColumnDisabled]="!column.sortable"
        >
          <div class="th-content">
            <span [innerHTML]="column.header | safeHtml"> </span>
            @if (column.headerTooltip) {
              <i
                aria-hidden="true"
                class="pi pi-exclamation-triangle th-content__warning"
                [pTooltip]="column.headerTooltip"
                tooltipPosition="top"
              >
              </i>
            }
            @if (column.sortable) {
              <p-sorticon [field]="column.field"></p-sorticon>
            }
          </div>
        </th>
      }
      @if (duplicatable) {
        <th class="column column--options"></th>
      }
      @if (deletable || editable()) {
        <th class="column column--options column--actions"></th>
      }
      @if (reorderable()) {
        <th class="column column--reorder"></th>
      }
    </tr>
  </ng-template>

  <ng-template #body let-rowData let-rowIndex="rowIndex">
    @if (isSkeletonRow(rowData)) {
      <tr aria-hidden="true">
        @if (selectable()) {
          <td class="column--selection"><p-skeleton width="1rem" /></td>
        }
        @for (column of visibleColumns(); track column) {
          <td>
            <p-skeleton
              [width]="isNarrowColumn(column.type) ? '1.5rem' : '80%'"
            />
          </td>
        }
        @if (duplicatable) {
          <td class="column column--options"><p-skeleton width="1.5rem" /></td>
        }
        @if (deletable || editable()) {
          <td class="column column--options column--actions">
            <p-skeleton width="1.5rem" />
          </td>
        }
        @if (reorderable()) {
          <td class="column column--reorder"><p-skeleton width="1.5rem" /></td>
        }
      </tr>
    } @else {
      <tr
        aria-hidden="true"
        (click)="onClickRow(rowIndex, $event)"
        [pReorderableRow]="rowIndex"
        [pSelectableRow]="rowData"
        [pSelectableRowIndex]="rowIndex"
        [pSelectableRowDisabled]="true"
        [pContextMenuRow]="rowData"
        [class]="rowClass ? rowClass(rowData) : ''"
        [class.p-editor-enabled]="editingIndex === rowIndex"
      >
        <!-- SELECTABLE -->
        @if (selectable()) {
          <td
            class="column--selection"
            (click)="onClickSelectionCell(rowData, rowIndex, $event)"
          >
            @if (rowData.isBusy) {
              <p-progressspinner></p-progressspinner>
            } @else {
              @if (rowSelectable ? rowSelectable(rowData) : true) {
                <p-tablecheckbox
                  [value]="rowData"
                  [disabled]="editingIndex === rowIndex"
                >
                </p-tablecheckbox>
              }
            }
          </td>
        }
        @for (column of visibleColumns(); track column) {
          @switch (column.type) {
            <!-- SERVICE -->
            @case (tableColumn.service) {
              <td
                [attr.title]="
                  column.displayValue ? column.displayValue(rowData) : null
                "
              >
                @if (editingIndex === rowIndex) {
                  @let cellDisabled = isCellDisabled(column, rowData);
                  <div
                    class="cell-editor"
                    [class.cell-editor--invalid]="
                      isCellInvalid(column, editingRowData)
                    "
                  >
                    @if (showRequiredMarker(column, cellDisabled)) {
                      <span class="required-marker" aria-hidden="true"></span>
                    }
                    <app-carrier-select
                      [disabled]="cellDisabled"
                      [required]="column.required"
                      [showAll]="!column.required"
                      [kind]="column.kind"
                      [carrier]="editingRowData[column.field]"
                      [options]="
                        column.getOptions
                          ? column.getOptions(editingRowData)
                          : null
                      "
                      (carrierChange)="editingRowData[column.field] = $event"
                    >
                    </app-carrier-select>
                  </div>
                }
                @if (editingIndex !== rowIndex) {
                  {{
                    column.displayValue
                      ? column.displayValue(rowData)
                      : rowData[column.field]?.name || "All"
                  }}
                }
              </td>
            }
            <!-- SERVICES -->
            @case (tableColumn.services) {
              <td>
                @if (editingIndex === rowIndex) {
                  @let cellDisabled = isCellDisabled(column, rowData);
                  <div class="cell-editor">
                    @if (showRequiredMarker(column, cellDisabled)) {
                      <span class="required-marker" aria-hidden="true"></span>
                    }
                    <app-carrier-multiselect
                      [disabled]="cellDisabled"
                      [required]="column.required"
                      [kind]="column.kind"
                      [originCountry]="editingRowData['originCountry']"
                      [destinationCountry]="
                        editingRowData['destinationCountry']
                      "
                      [discriminator]="column.discriminator"
                      [carriers]="editingRowData[column.field]"
                      (carrierChange)="editingRowData[column.field] = $event"
                    >
                    </app-carrier-multiselect>
                  </div>
                }
                @if (editingIndex !== rowIndex) {
                  @if (
                    !(rowData[column.field] && rowData[column.field].length)
                  ) {
                    <span> All </span>
                  }
                  @for (
                    item of rowData[column.field];
                    track item;
                    let last = $last
                  ) {
                    <span> {{ item.name }}{{ !last ? ", " : "" }} </span>
                  }
                }
              </td>
            }
            <!-- RATES -->
            @case (tableColumn.rates) {
              <td>
                @if (editingIndex === rowIndex) {
                  @let cellDisabled = isCellDisabled(column, rowData);
                  <div class="cell-editor">
                    @if (showRequiredMarker(column, cellDisabled)) {
                      <span class="required-marker" aria-hidden="true"></span>
                    }
                    <app-rate-multiselect
                      [disabled]="cellDisabled"
                      [required]="column.required"
                      [kind]="column.kind"
                      [rates]="editingRowData[column.field]"
                      (rateChange)="editingRowData[column.field] = $event"
                    >
                    </app-rate-multiselect>
                  </div>
                }
                @if (editingIndex !== rowIndex) {
                  @if (
                    !(rowData[column.field] && rowData[column.field].length)
                  ) {
                    <span> All </span>
                  }
                  @for (
                    item of rowData[column.field];
                    track item;
                    let last = $last
                  ) {
                    <span> {{ item.name }}{{ !last ? ", " : "" }} </span>
                  }
                }
              </td>
            }
            <!-- SURCHARGE-TYPE -->
            @case (tableColumn.surchargeType) {
              <td
                pTooltip="{{ rowData[column.field]?.name }}"
                showDelay="1000"
                [tooltipDisabled]="editingIndex === rowIndex"
              >
                @if (editingIndex === rowIndex) {
                  @let cellDisabled = isCellDisabled(column, rowData);
                  <div class="cell-editor">
                    @if (showRequiredMarker(column, cellDisabled)) {
                      <span class="required-marker" aria-hidden="true"></span>
                    }
                    <app-surcharge-type-select
                      [required]="column.required"
                      [surchargeType]="editingRowData[column.field]"
                      (surchargeTypeChange)="
                        editingRowData[column.field] = $event
                      "
                    >
                    </app-surcharge-type-select>
                  </div>
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field]?.name }}
                }
              </td>
            }
            <!-- SURCHARGE-TYPE-AUTOCOMPLETE -->
            @case (tableColumn.surchargeTypeAutocomplete) {
              <td
                pTooltip="{{ rowData['debugInfoParsed'] }}"
                [tooltipOptions]="{ escape: false }"
                showDelay="1000"
                [tooltipDisabled]="editingIndex === rowIndex"
              >
                @if (editingIndex === rowIndex) {
                  <app-surcharge-type-autocomplete
                    [surchargeType]="editingRowData[column.field]"
                    (surchargeTypeChange)="
                      editingRowData[column.field] = $event
                    "
                  >
                  </app-surcharge-type-autocomplete>
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- ORGANIZATION -->
            @case (tableColumn.organization) {
              <td>
                {{ rowData[column.field]?.name }}
              </td>
            }
            <!-- CLIENT -->
            @case (tableColumn.client) {
              <td>
                {{ rowData[column.field]?.name }}
              </td>
            }
            <!-- TEAM -->
            @case (tableColumn.team) {
              <td>
                {{ rowData[column.field]?.name }}
              </td>
            }
            <!-- LOAD-LOCATION -->
            @case (tableColumn.loadLocation) {
              <td>
                @if (rowData[column.field]?.kind) {
                  <app-location-icon
                    [kind]="
                      $safeNavigationMigration(rowData[column.field]?.kind)
                    "
                  >
                  </app-location-icon>
                }
                {{ rowData[column.field]?.load }}
              </td>
            }
            <!-- LOCATION -->
            @case (tableColumn.location) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (!column.options) {
                    <app-location-select
                      [appendTo]="'body'"
                      [locationKindFilterOptions]="column.kind"
                      [location]="editingRowData[column.field]"
                      (locationChange)="editingRowData[column.field] = $event"
                    >
                    </app-location-select>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  <app-location-icon
                    [kind]="
                      $safeNavigationMigration(rowData[column.field]?.getKind())
                    "
                  >
                  </app-location-icon>
                  @if (rowData[column.field]?.getName()) {
                    <span>{{ rowData[column.field]?.getName() }}</span>
                  }
                }
              </td>
            }
            <!-- LOCATIONS -->
            @case (tableColumn.locationsWithIcon) {
              <td>
                @if (editingIndex !== rowIndex) {
                  @for (
                    item of rowData[column.field];
                    track item;
                    let last = $last
                  ) {
                    <span>
                      <app-location-icon
                        [kind]="$safeNavigationMigration(item?.getKind())"
                      ></app-location-icon>
                      {{ item.name }}{{ !last ? ", " : "" }}
                    </span>
                  }
                }
              </td>
            }
            <!-- COUNTRY -->
            @case (tableColumn.country) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (!column.options) {
                    @let cellDisabled = isCellDisabled(column, rowData);
                    <div class="cell-editor">
                      @if (showRequiredMarker(column, cellDisabled)) {
                        <span class="required-marker" aria-hidden="true"></span>
                      }
                      <app-country-select
                        [required]="column.required"
                        [country]="editingRowData[column.field]"
                        (countryChange)="editingRowData[column.field] = $event"
                      >
                      </app-country-select>
                    </div>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  @if (!rowData[column.field] && !column.required) {
                    <span> All </span>
                  }
                  @if (rowData[column.field]) {
                    <span>
                      ({{ rowData[column.field] }})
                      {{ rowData[column.field] | countryName }}
                    </span>
                  }
                }
              </td>
            }
            <!-- COUNTRIES -->
            @case (tableColumn.countries) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (!column.options) {
                    <app-country-multiselect
                      [countries]="editingRowData[column.field]"
                      (countryChange)="editingRowData[column.field] = $event"
                    >
                    </app-country-multiselect>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  {{
                    rowData[column.field] && rowData[column.field].length
                      ? rowData[column.field].join(", ")
                      : "All"
                  }}
                }
              </td>
            }
            <!-- LOAD TYPE -->
            @case (tableColumn.loadType) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (!column.options) {
                    <app-load-type-select
                      [loadType]="editingRowData[column.field]"
                      (loadTypeChange)="editingRowData[column.field] = $event"
                    >
                    </app-load-type-select>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- TRANSPORT TYPE -->
            @case (tableColumn.transportType) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (!column.options) {
                    <app-transport-type-select
                      [transportType]="editingRowData[column.field]"
                      (transportTypeChange)="
                        editingRowData[column.field] = $event
                      "
                    >
                    </app-transport-type-select>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- UNIT TYPE -->
            @case (tableColumn.unitType) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (!column.options) {
                    <app-unit-type-select
                      [unitType]="editingRowData[column.field]"
                      (unitTypeChange)="editingRowData[column.field] = $event"
                    >
                    </app-unit-type-select>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- MARGIN TYPE -->
            @case (tableColumn.marginType) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (!column.options) {
                    <app-margin-type-select
                      [marginType]="editingRowData[column.field]"
                      (marginTypeChange)="editingRowData[column.field] = $event"
                    >
                    </app-margin-type-select>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- RATE LOCATIONS (MULTIPLE) -->
            @case (tableColumn.rateLocations) {
              <td>
                @if (editingIndex === rowIndex) {
                  <p-multiselect
                    [selectAll]="null"
                    [options]="column.locations"
                    [(ngModel)]="editingRowData[column.field]"
                    showToggleAll="false"
                    maxSelectedLabels="0"
                    dataKey="id"
                    placeholder="All locations"
                    selectedItemsLabel="{0} selected"
                    appendTo="body"
                  >
                  </p-multiselect>
                }
                @if (editingIndex !== rowIndex) {
                  @if (!rowData[column.field].length) {
                    <span> All locations </span>
                  }
                  @for (
                    rateLocation of rowData[column.field];
                    track rateLocation;
                    let last = $last
                  ) {
                    <span>
                      {{ rateLocation.getLocation()?.getName()
                      }}{{ !last ? ", " : "" }}
                    </span>
                  }
                }
              </td>
            }
            <!-- LOCATIONS (MULTIPLE) -->
            @case (tableColumn.locations) {
              <td>
                @if (editingIndex === rowIndex) {
                  <p-multiselect
                    [selectAll]="null"
                    [options]="column.locations"
                    [(ngModel)]="editingRowData[column.field]"
                    showToggleAll="false"
                    maxSelectedLabels="0"
                    dataKey="id"
                    placeholder="All locations"
                    selectedItemsLabel="{0} selected"
                    appendTo="body"
                  >
                  </p-multiselect>
                }
                @if (editingIndex !== rowIndex) {
                  @if (!rowData[column.field].length) {
                    <span> All locations </span>
                  }
                  @for (
                    location of rowData[column.field];
                    track location;
                    let last = $last
                  ) {
                    <span>
                      {{ location.getName() }}{{ !last ? ", " : "" }}
                    </span>
                  }
                }
              </td>
            }
            <!-- PARENT -->
            @case (tableColumn.parent) {
              <td>
                @if (editingIndex === rowIndex) {
                  <p-select
                    [(ngModel)]="editingRowData[column.field]"
                    [options]="column.locations"
                    [filter]="true"
                    filterBy="label, value"
                    appendTo="body"
                    dataKey="id"
                  >
                    <ng-template let-parent #item>
                      @if (!!parent.value) {
                        <app-location-icon
                          [kind]="parent.value.getLocation().getKind()"
                        >
                        </app-location-icon>
                      }
                      {{ parent.label }}
                    </ng-template>
                  </p-select>
                }
                @if (editingIndex !== rowIndex) {
                  @if (!!rowData[column.field]?.getLocation()) {
                    <app-location-icon
                      [kind]="
                        $safeNavigationMigration(
                          rowData[column.field]?.getLocation()?.getKind()
                        )
                      "
                    >
                    </app-location-icon>
                  }
                  <span>{{
                    rowData[column.field]?.getLocation()?.getName() ||
                      "Base Location"
                  }}</span>
                }
              </td>
            }
            <!-- USER NAMES -->
            @case (tableColumn.users) {
              <td>
                @for (
                  user of rowData[column.field];
                  track user;
                  let last = $last
                ) {
                  <span data-cy="client-sales-rep">
                    {{ user.getFullName() }}{{ !last ? ", " : "" }}
                  </span>
                }
              </td>
            }
            <!-- USER GROUP -->
            @case (tableColumn.group) {
              <td>
                @for (
                  element of rowData.getGroups();
                  track element;
                  let last = $last
                ) {
                  <span> {{ element.getName() }} {{ last ? "" : ", " }} </span>
                }
              </td>
            }
            <!-- BOOLEAN -->
            @case (tableColumn.boolean) {
              <td>
                @if (editingIndex !== rowIndex) {
                  <i
                    class="pi qs-boolean"
                    [class.pi-check-circle]="rowData[column.field]"
                    [class.pi-circle]="!rowData[column.field]"
                  >
                  </i>
                }
                @if (editingIndex === rowIndex) {
                  <i
                    aria-hidden="true"
                    class="pi qs-boolean editable"
                    (click)="
                      editingRowData[column.field] =
                        !editingRowData[column.field]
                    "
                    [class.pi-check-circle]="editingRowData[column.field]"
                    [class.pi-circle]="!editingRowData[column.field]"
                  >
                  </i>
                }
              </td>
            }
            <!-- OPENED -->
            @case (tableColumn.opened) {
              <td class="column--icon">
                <i
                  class="pi qs-boolean"
                  [class.pi-envelope]="rowData[column.field]"
                  [class.pi-envelope]="
                    !rowData[column.field] && rowData['status'] !== 'draft'
                  "
                  pTooltip="{{
                    rowData[column.field] | date: 'd-MM-YYYY H:mm:ss'
                  }}"
                >
                </i>
              </td>
            }
            <!-- DOWNLOAD -->
            @case (tableColumn.download) {
              <td class="column--icon">
                @if (rowData[column.field]) {
                  <a
                    aria-hidden="true"
                    class="pi pi-download"
                    target="_blank"
                    [href]="rowData[column.field]"
                    (click)="$event.stopPropagation()"
                  >
                  </a>
                }
              </td>
            }
            <!-- COPY -->
            @case (tableColumn.copy) {
              <td class="column--icon">
                <app-clipboard-row
                  [textToBeCopied]="rowData[column.field]"
                ></app-clipboard-row>
              </td>
            }
            <!-- EDITABLE -->
            @case (tableColumn.editable) {
              <td class="column--icon">
                <i
                  class="pi qs-boolean"
                  [class.pi-lock]="!rowData[column.field]"
                >
                </i>
              </td>
            }
            <!-- DATE -->
            @case (tableColumn.date) {
              <td>
                {{ rowData[column.field] | date: "d.MM.yyyy" }}
              </td>
            }
            <!-- DATETIME -->
            @case (tableColumn.datetime) {
              <td>
                {{ rowData[column.field] | date: "d.MM.yy HH:mm" }}
              </td>
            }
            <!-- COMMENT -->
            @case (tableColumn.comment) {
              <td class="column--comment">
                @if (editingIndex === rowIndex) {
                  <i
                    aria-hidden="true"
                    class="pi pi-comment qs-full-size"
                    (click)="notePanel.toggle($event)"
                  >
                  </i>
                  <p-popover
                    appPopoverArrow
                    #notePanel
                    [dismissable]="true"
                    appendTo="body"
                  >
                    @if (notePanel.overlayVisible()) {
                      @let cellDisabled =
                        isCellDisabled(column, editingRowData);
                      <app-wysiwyg
                        [value]="editingRowData[column.field]"
                        [readonly]="cellDisabled"
                        (valueChanged)="
                          cellDisabled ||
                            (editingRowData[column.field] = $event)
                        "
                      >
                      </app-wysiwyg>
                    }
                  </p-popover>
                }
                @if (editingIndex !== rowIndex) {
                  @if (rowData[column.field]) {
                    <i
                      aria-hidden="true"
                      class="pi pi-comments qs-full-size"
                      (click)="openOverlay($event, rowData[column.field], op)"
                    >
                    </i>
                  }
                  @if (!rowData[column.field]) {
                    <i class="pi pi-comment qs-full-size opacity"> </i>
                  }
                }
              </td>
            }
            <!-- EDITABLE COMMENT -->
            @case (tableColumn.editableComment) {
              <td class="column--comment">
                <i
                  aria-hidden="true"
                  class="pi qs-full-size"
                  [class.pi-comments]="rowData[column.field]"
                  [class.pi-comment]="!rowData[column.field]"
                  (click)="$event.stopPropagation(); notePanel.toggle($event)"
                >
                </i>
                <p-popover
                  appPopoverArrow
                  #notePanel
                  [dismissable]="true"
                  (onHide)="saveRow.emit(rowData)"
                  appendTo="body"
                >
                  @if (notePanel.overlayVisible()) {
                    <textarea
                      pInputText
                      [(ngModel)]="rowData[column.field]"
                      class="form-control"
                    >
                    </textarea>
                  }
                </p-popover>
              </td>
            }
            <!-- WARNING -->
            @case (tableColumn.warning) {
              <td class="column--warning">
                @if (rowData[column.field]) {
                  <i
                    aria-hidden="true"
                    class="pi pi-exclamation-triangle qs-full-size"
                    (click)="openOverlay($event, rowData[column.field], op)"
                  >
                  </i>
                }
              </td>
            }
            <!-- INFO -->
            @case (tableColumn.info) {
              <td class="column--info">
                @if (rowData[column.field]) {
                  <i
                    aria-hidden="true"
                    class="pi pi-info-circle qs-full-size"
                    (click)="openOverlay($event, rowData[column.field], op)"
                  >
                  </i>
                }
              </td>
            }
            <!-- LINK -->
            @case (tableColumn.link) {
              <td class="column--link">
                @if (rowData[column.field]) {
                  <a
                    aria-hidden="true"
                    [href]="rowData[column.field]"
                    (click)="$event.stopPropagation()"
                    target="_blank"
                  >
                    <i class="pi pi-external-link"></i>
                  </a>
                }
              </td>
            }
            <!-- TEXT LINK -->
            @case (tableColumn.textLink) {
              <td class="column--textLink">
                @let textLinkHref = column.href?.(rowData);
                @if (textLinkHref) {
                  <a
                    class="text-link"
                    [href]="textLinkHref"
                    (click)="$event.stopPropagation()"
                    target="_blank"
                  >
                    {{ rowData[column.field] }}
                  </a>
                } @else {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- DEBUG -->
            @case (tableColumn.debug) {
              <td class="column--debug">
                @if (rowData[column.field]) {
                  <i
                    aria-hidden="true"
                    class="pi pi-calculator qs-full-size"
                    (click)="openOverlay($event, rowData[column.field], op)"
                  >
                  </i>
                }
              </td>
            }
            <!-- QUOTE DETAILS -->
            @case (tableColumn.quote) {
              <td
                [pTooltip]="quoteTooltip"
                tooltipPosition="top"
                tooltipStyleClass="p-tooltip--wide"
                showOnEllipsis
                appTooltipOnEllipsis
                showDelay="1000"
                [tooltipDisabled]="!hasDetailsTooltip(rowData)"
              >
                <app-quote-list-row [quote]="rowData"> </app-quote-list-row>
                <ng-template #quoteTooltip>
                  <app-quote-list-row [quote]="rowData" />
                </ng-template>
              </td>
            }
            @case (tableColumn.relatedQuote) {
              <td
                [pTooltip]="relatedQuoteTooltip"
                tooltipPosition="top"
                tooltipStyleClass="p-tooltip--wide"
                showOnEllipsis
                appTooltipOnEllipsis
                showDelay="1000"
                [tooltipDisabled]="!hasDetailsTooltip(rowData[column.field])"
              >
                @if (rowData[column.field]) {
                  <app-quote-list-row [quote]="rowData[column.field]">
                  </app-quote-list-row>
                }
                <ng-template #relatedQuoteTooltip>
                  <app-quote-list-row [quote]="rowData[column.field]" />
                </ng-template>
              </td>
            }
            <!-- QUOTE SOURCE -->
            @case (tableColumn.quoteSource) {
              <td class="column--quoteSource">
                <span
                  class="quote-source quote-source--{{ rowData[column.field] }}"
                >
                  <span>
                    @switch (rowData[column.field]) {
                      @case (quoteSource.online) {
                        Online
                      }
                      @case (quoteSource.internalEmail) {
                        Internal Email
                      }
                      @case (quoteSource.externalEmail) {
                        External Email
                      }
                      @case (null) {
                        Quotiss User
                      }
                    }
                  </span>
                  @if (
                    rowData[column.field] === quoteSource.internalEmail ||
                    rowData[column.field] === quoteSource.externalEmail
                  ) {
                    <i class="pi pi-sparkles" aria-hidden="true"></i>
                  }
                </span>
              </td>
            }
            <!-- STATUS (AND REQUEST INFO) -->
            @case (tableColumn.status) {
              <td class="column--status">
                <span
                  data-cy="status"
                  class="status status--{{
                    rowData[column.field]
                  }} requestInfo--{{ rowData['requestInfo']?.type }}"
                >
                  @if (!rowData["requestInfo"]) {
                    <span>{{ rowData[column.field] }}</span>
                  }
                  @if (rowData["requestInfo"]) {
                    <span [pTooltip]="rowData['requestInfo'].msg">
                      {{ rowData["requestInfo"].type }}
                    </span>
                  }
                </span>
              </td>
            }
            <!-- TIME AGO -->
            @case (tableColumn.timeAgo) {
              <td>
                @if (isToday(rowData[column.field])) {
                  <span>{{ rowData[column.field] | timeAgo }}</span>
                }
                @if (!isToday(rowData[column.field])) {
                  <span>{{ rowData[column.field] | date: "d.MM.yyyy" }}</span>
                }
              </td>
            }
            <!-- DATE PICKER -->
            @case (tableColumn.datePicker) {
              <td>
                @if (editingIndex === rowIndex) {
                  @let cellDisabled = isCellDisabled(column, editingRowData);
                  @let editingWarning =
                    column.warning ? column.warning(editingRowData) : "";
                  <div
                    class="cell-editor"
                    [class.cell-editor--invalid]="
                      isCellInvalid(column, editingRowData)
                    "
                  >
                    @if (showRequiredMarker(column, cellDisabled)) {
                      <span class="required-marker" aria-hidden="true"></span>
                    }
                    <div
                      class="cell-editor__picker"
                      [class.cell-editor__picker--warned]="editingWarning"
                    >
                      <p-datepicker
                        [(ngModel)]="editingRowData[column.field]"
                        [required]="column.required"
                        [disabled]="cellDisabled"
                        appendTo="body"
                        selectionMode="range"
                        dateFormat="dd.mm.y"
                        [firstDayOfWeek]="1"
                      ></p-datepicker>
                      @if (editingWarning) {
                        <i
                          aria-hidden="true"
                          class="pi pi-exclamation-triangle cell-warning"
                          (click)="openOverlay($event, editingWarning, op)"
                        >
                        </i>
                      }
                    </div>
                  </div>
                }
                @if (editingIndex !== rowIndex) {
                  @let rowWarning =
                    column.warning ? column.warning(rowData) : "";
                  <div class="cell-value">
                    @if (rowData[column.field]) {
                      <span>
                        @if (rowData[column.field][0]) {
                          <span>{{
                            rowData[column.field][0] | date: "d.MM.yy"
                          }}</span>
                        }
                        @if (rowData[column.field][1]) {
                          <span>
                            -
                            {{
                              rowData[column.field][1] | date: "d.MM.yy"
                            }}</span
                          >
                        }
                      </span>
                    }
                    @if (rowWarning) {
                      <i
                        aria-hidden="true"
                        class="pi pi-exclamation-triangle cell-warning"
                        (click)="openOverlay($event, rowWarning, op)"
                      >
                      </i>
                    }
                  </div>
                }
              </td>
            }
            <!-- DIRECTION -->
            @case (tableColumn.direction) {
              <td>
                @if (editingIndex === rowIndex) {
                  <app-direction-select
                    [direction]="editingRowData[column.field]"
                    (directionChange)="editingRowData[column.field] = $event"
                  >
                  </app-direction-select>
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- CURRENCY -->
            @case (tableColumn.currency) {
              <td>
                @if (editingIndex === rowIndex) {
                  <app-currency-select
                    [disabled]="isCellDisabled(column, rowData)"
                    [currency]="editingRowData[column.field]"
                    (currencyChange)="editingRowData[column.field] = $event"
                  >
                  </app-currency-select>
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- CONTAINER VALUE -->
            @case (tableColumn.container) {
              <td>
                @if (editingIndex === rowIndex) {
                  <input
                    pInputText
                    type="text"
                    (change)="
                      editingRowData[column.field].setPrice(
                        column.containerName,
                        $event.target.value
                      )
                    "
                    [value]="
                      rowData[column.field].getPrice(column.containerName)
                    "
                  />
                }
                @if (editingIndex !== rowIndex) {
                  {{
                    rowData[column.field]?.getPrice(column.containerName) ||
                    rowData[column.field]?.getPrice(column.containerName) === 0
                      ? ($safeNavigationMigration(
                          rowData[column.field]?.getPrice(column.containerName)
                        ) | currency)
                      : "-"
                  }}
                }
              </td>
            }
            <!-- PRICE-BOX -->
            @case (tableColumn.priceBox) {
              <td>
                @if (editingIndex === rowIndex) {
                  @if (
                    !(
                      column.hideWhenEmpty &&
                      column.getNet(editingRowData, column.field) === null
                    )
                  ) {
                    @let cellDisabled = isCellDisabled(column, rowData);
                    <app-price-box
                      [displayKgAsTon]="column.displayKgAsTon"
                      [nullable]="isPriceBoxNullable(rowData) && !cellDisabled"
                      [isNetDisabled]="cellDisabled"
                      [multiplier]="
                        column.getMultiplier
                          ? column.getMultiplier(editingRowData, column.field)
                          : null
                      "
                      [net]="column.getNet(editingRowData, column.field)"
                      (netChange)="
                        column.setNet(editingRowData, column.field, $event)
                      "
                      [margin]="column.getMargin(editingRowData, column.field)"
                      (marginChange)="
                        column.setMargin(editingRowData, column.field, $event)
                      "
                    >
                    </app-price-box>
                  }
                }
                @if (editingIndex !== rowIndex) {
                  <app-price-box
                    [displayMargin]="
                      column.getMargin(rowData, column.field) !== undefined
                    "
                    [displayKgAsTon]="column.displayKgAsTon"
                    [nullable]="isPriceBoxNullable(rowData)"
                    [editable]="false"
                    [net]="column.getNet(rowData, column.field)"
                    [margin]="column.getMargin(rowData, column.field)"
                    [multiplier]="
                      column.getMultiplier
                        ? column.getMultiplier(rowData, column.field)
                        : null
                    "
                  >
                  </app-price-box>
                }
              </td>
            }
            <!-- PERCENT-BOX -->
            @case (tableColumn.percentBox) {
              <td>
                @if (editingIndex === rowIndex) {
                  <app-percent-box
                    [nullable]="isPriceBoxNullable(rowData)"
                    [isNetDisabled]="isCellDisabled(column, rowData)"
                    [net]="column.getNet(editingRowData, column.field)"
                    (netChange)="
                      column.setNet(editingRowData, column.field, $event)
                    "
                    [margin]="column.getMargin(editingRowData, column.field)"
                    (marginChange)="
                      column.setMargin(editingRowData, column.field, $event)
                    "
                  >
                  </app-percent-box>
                }
                @if (editingIndex !== rowIndex) {
                  <app-percent-box
                    [displayMargin]="
                      column.getMargin(rowData, column.field) !== undefined
                    "
                    [nullable]="isPriceBoxNullable(rowData)"
                    [editable]="false"
                    [net]="column.getNet(rowData, column.field)"
                    [margin]="column.getMargin(rowData, column.field)"
                  >
                  </app-percent-box>
                }
              </td>
            }
            <!-- WEIGHT IN TONS -->
            @case (tableColumn.weightInTon) {
              <td>
                @if (editingIndex === rowIndex) {
                  <input
                    pInputText
                    type="text"
                    [value]="editingRowData[column.field] * 1000 | currency"
                    (keyup)="
                      editingRowData[column.field] = $event.target.value / 1000;
                      onEditRow(column)
                    "
                  />
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] * 1000 | currency }}
                }
              </td>
            }
            <!-- DISABLED -->
            @case (tableColumn.disabled) {
              <td>
                @if (editingIndex === rowIndex) {
                  <input
                    pInputText
                    type="text"
                    disabled="disabled"
                    value="{{ editingRowData[column.field] }}"
                  />
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
            <!-- NUMBER -->
            @case (tableColumn.number) {
              <td>
                @if (editingIndex === rowIndex) {
                  <input
                    pInputText
                    type="number"
                    [disabled]="isCellDisabled(column, rowData)"
                    [(ngModel)]="editingRowData[column.field]"
                    (keyup)="onEditRow(column)"
                  />
                }
                @if (editingIndex !== rowIndex) {
                  @if (!rowData[column.field]) {
                    <span> &mdash; </span>
                  }
                  @if (rowData[column.field]) {
                    <span>
                      {{ rowData[column.field] | currency }}
                    </span>
                  }
                }
              </td>
            }
            <!-- NUMBER DISPLAY ALSO ZERO -->
            @case (tableColumn.numberDisplayAlsoZero) {
              <td>
                @if (editingIndex === rowIndex) {
                  <input
                    pInputText
                    type="number"
                    [disabled]="isCellDisabled(column, rowData)"
                    [(ngModel)]="editingRowData[column.field]"
                    (keyup)="onEditRow(column)"
                  />
                }
                @if (editingIndex !== rowIndex) {
                  @if (!rowData[column.field] && rowData[column.field] !== 0) {
                    <span> - </span>
                  }
                  @if (rowData[column.field] || rowData[column.field] === 0) {
                    <span>
                      {{ rowData[column.field] | currency }}
                    </span>
                  }
                }
              </td>
            }
            <!-- PASSWORD -->
            @case (tableColumn.password) {
              <td
                pTooltip="{{ rowData[column.field] }}"
                showDelay="1000"
                [tooltipDisabled]="editingIndex === rowIndex"
              >
                @if (editingIndex === rowIndex) {
                  <input
                    pInputText
                    type="text"
                    autocomplete="off"
                    class="input-secret"
                    [disabled]="isCellDisabled(column, rowData)"
                    [(ngModel)]="editingRowData[column.field]"
                    (keyup)="onEditRow(column)"
                  />
                }
                @if (editingIndex !== rowIndex) {
                  *******
                }
              </td>
            }
            <!-- DEFAULT VALUE -->
            @default {
              <td
                pTooltip="{{ rowData[column.field] }}"
                tooltipPosition="top"
                showOnEllipsis
                appTooltipOnEllipsis
                showDelay="1000"
                [tooltipDisabled]="editingIndex === rowIndex"
              >
                @if (editingIndex === rowIndex) {
                  @let cellDisabled = isCellDisabled(column, rowData);
                  <div class="cell-editor">
                    @if (showRequiredMarker(column, cellDisabled)) {
                      <span class="required-marker" aria-hidden="true"></span>
                    }
                    <input
                      pInputText
                      autocomplete="new-password"
                      autofill="off"
                      type="text"
                      [required]="column.required"
                      [disabled]="cellDisabled"
                      [(ngModel)]="editingRowData[column.field]"
                      (keyup)="onEditRow(column)"
                    />
                  </div>
                }
                @if (editingIndex !== rowIndex) {
                  {{ rowData[column.field] }}
                }
              </td>
            }
          }
        }

        <!-- SAVE / DELETE / CLOSE -->
        @if (duplicatable) {
          <td>
            @if (editingIndex !== rowIndex) {
              @if (duplicatable) {
                <button
                  pButton
                  data-cy="duplicate"
                  severity="success"
                  size="small"
                  [fluid]="true"
                  (click)="onDuplicateRow($event, rowIndex)"
                >
                  Duplicate
                </button>
              }
            }
          </td>
        }

        <!-- SAVE / DELETE / CLOSE -->
        @if (deletable || editable()) {
          <td class="column--actions">
            @if (editingIndex === rowIndex) {
              <div class="options floating-options">
                <button
                  pButton
                  data-cy="close"
                  severity="danger"
                  size="large"
                  (click)="onCancelRow($event)"
                >
                  @if (isNewRow(rowData)) {
                    <i class="pi pi-trash"></i>
                  } @else {
                    <i class="pi pi-undo"></i>
                  }
                </button>
                <button
                  pButton
                  data-cy="save"
                  severity="success"
                  size="large"
                  [disabled]="!editingRowData.isValid()"
                  (click)="onSaveRow($event)"
                >
                  <i class="pi pi-check"></i>
                </button>
              </div>
            }
            @if (editingIndex !== rowIndex) {
              <div class="options">
                @if (deletable) {
                  <button
                    pButton
                    data-cy="delete"
                    severity="danger"
                    size="small"
                    [fluid]="true"
                    (click)="onDeleteRow($event, rowIndex)"
                  >
                    Delete
                  </button>
                }
              </div>
            }
          </td>
        }

        <!-- REORDER -->
        @if (reorderable()) {
          <td>
            <i class="pi pi-bars" pReorderableRowHandle></i>
          </td>
        }
      </tr>
    }
  </ng-template>

  <ng-template #paginatorleft>
    @if (showRecordCounter()) {
      <span class="record-counter" data-cy="record-counter">
        {{ recordCounterLabel() }}
      </span>
    }
  </ng-template>

  <ng-template #emptymessage let-columns>
    <tr>
      <td [attr.colspan]="getColumnCount()" class="empty-message">
        <span class="empty-message__sticky">
          <app-empty-message [text]="emptyMessage()"></app-empty-message>
        </span>
      </td>
    </tr>
  </ng-template>

  <ng-template #footer let-columns>
    @if (paginator() && addPosition() === "bottom" && addable && newValue()) {
      <tr>
        <td [attr.colspan]="getColumnCount()" class="add-new-item">
          <button
            pButton
            data-cy="add"
            size="small"
            class="table-actions__add"
            (click)="onAddRow()"
          >
            <i class="pi pi-plus"></i> {{ addLabel() }}
          </button>
        </td>
      </tr>
    }
  </ng-template>
</p-table>

@if (
  !paginator() &&
  newValue() &&
  ((addPosition() === "bottom" && addable) || onlineCarriers().length)
) {
  <div class="table-actions">
    @if (addPosition() === "bottom" && addable) {
      <button
        pButton
        data-cy="add"
        size="small"
        class="table-actions__add"
        (click)="onAddRow()"
      >
        <i class="pi pi-plus"></i> {{ addLabel() }}
      </button>
    }
    @if (onlineCarriers().length) {
      <div class="table-actions__check">
        <span class="table-actions__check-label">Live rate search:</span>
        <app-online-carrier-info></app-online-carrier-info>
        <div class="table-actions__check-buttons">
          <button
            pButton
            data-cy="search-all"
            size="small"
            [outlined]="true"
            [disabled]="!searchAllCarriers().length"
            (click)="onSearchAll()"
          >
            Search all
          </button>
          @for (carrier of onlineCarriers(); track carrier.type) {
            <app-online-carrier-button
              [label]="carrier.label"
              [status]="carrier.status"
              [loading]="carrier.loading"
              [loadingLabel]="carrier.loadingLabel || 'Loading...'"
              [disabled]="carrier.disabled"
              (loadRates)="onLoadOnlineCarrier(carrier.type)"
            ></app-online-carrier-button>
          }
        </div>
      </div>
    }
  </div>
}

<p-contextmenu #cm [model]="contextMenuItems()"></p-contextmenu>
<p-popover appPopoverArrow appendTo="body" #op [dismissable]="true">
  <div [innerHTML]="overlayContent | safeHtml"></div>
</p-popover>
